import { CSSProperties, useEffect, useRef } from 'react';
import { Paper } from '@mui/material';
import { SMALL_CARD_HEIGHT, SMALL_CARD_WIDTH } from 'constants/gameConstants';
import gsap from 'gsap';
import { useTableSelector } from 'hooks/useTableSelector';
import { useTableSnap } from 'hooks/useTableSnap';
import { shouldDimCard } from 'utils/functions';
import { COMMUNITY_FLIP_DELAY_S, COMMUNITY_FLIP_HALF_S } from 'utils/tableSfx';

import {
  CARD_BACK_ALT,
  CARD_BACK_SRC,
  cardAltText,
  cardCornerRadiusForWidth,
  cardFaceSrc
} from './deck';
import { Card } from '.';

type CardPropType = Card & {
  isFaceDown?: boolean;
  elevation?: number;
  trailRef?: any;
  card: string;
  /** Skip the flip animation — show face-up immediately (for cards already on table) */
  skipFlip?: boolean;
  /**
   * Run-it-twice: this card sits on a board that is not the one being paid now
   * — either already settled, or pre-dealt and still to come (AC-6.3 puts every
   * run-out on the felt before payout 1). Those boards are dim so the run whose
   * money is moving is the one that reads as live.
   *
   * Dimming is the ONLY treatment they get. It used to also draw them at 80%
   * (PAST_RUN_SCALE), which is removed — AC-7.2 keeps the on-felt presentation
   * minimal and invents no new visual treatments, and every board of a multi-run
   * is now the same size whether it has been paid or not.
   */
  isPastRun?: boolean;
};

/**
 * Community card with delayed flip animation.
 * Starts face-down, flips after 0.8s delay (matching Unity card reveal timing).
 *
 * Face and back are single artworks from the deck (see ./deck.ts); this
 * component only sizes, flips and dims them.
 */
const CommunityCard = ({
  suit,
  rank,
  trailRef,
  card,
  skipFlip = false,
  isPastRun = false
}: CardPropType) => {
  const game = useTableSelector((table) => table.game);
  const frontRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLDivElement>(null);
  const shouldSnap = useTableSnap();
  // True once this slot has shown (or scheduled the flip to) its face. When the
  // card VALUE later changes on the same mounted instance (the engine resends a
  // corrected board), the effect re-runs via the `card` dep and reconciles to
  // face-up WITHOUT replaying the flip. Before this, the effect never re-ran
  // for a value change, so a slot whose first value was unrenderable stayed a
  // card back for the whole hand even after real data arrived (the latch half
  // of the five-HJ-backs bug — see docs/investigations/
  // 20260821-community-cards-facedown-after-resume.md).
  const hasRevealedRef = useRef(false);

  // Any board other than the one being paid is dim outright: it must not be
  // judged against `pots`/`showdown`, which describe only the board being
  // settled right now.
  const isDimmed = shouldDimCard(game, card, isPastRun);

  useEffect(() => {
    const front = frontRef.current;
    const back = backRef.current;
    // No face to show (unparseable/masked value): nothing is rendered for this
    // slot (see JSX below), so there is nothing to animate. Do NOT mark it
    // revealed — when a real card arrives it should still get its flip.
    if (!front || !back) return;

    // `shouldSnap()`: switching into a table mid-hand mounts the whole board at
    // once, and every card would otherwise flip as if it had just been dealt.
    // The board is history at that point — show it face-up, same as skipFlip.
    // `hasRevealedRef`: a value change on an already-revealed slot reconciles
    // silently instead of re-flipping.
    if (skipFlip || shouldSnap() || hasRevealedRef.current) {
      // Already on table — show face-up immediately
      gsap.set(front, { scaleX: 1 });
      gsap.set(back, { scaleX: 0 });
      hasRevealedRef.current = true;
      return;
    }

    hasRevealedRef.current = true;

    // Start face-down
    gsap.set(front, { scaleX: 0 });
    gsap.set(back, { scaleX: 1 });

    // Dramatic flip: slight lift, flip, settle
    // Timing comes from tableSfx so the reveal CUE stays glued to this flip —
    // the cue is delayed by COMMUNITY_FLIP_DELAY_S + COMMUNITY_FLIP_HALF_S,
    // which is the moment the face below becomes readable. Change one, both move.
    const tl = gsap.timeline({ delay: COMMUNITY_FLIP_DELAY_S });
    tl.to(back, { scaleX: 0, scaleY: 1.08, duration: COMMUNITY_FLIP_HALF_S, ease: 'power2.in' })
      .to(front, { scaleX: 1, scaleY: 1.08, duration: COMMUNITY_FLIP_HALF_S, ease: 'power2.out' })
      .to(front, { scaleY: 1, duration: 0.15, ease: 'power2.out' });

    return () => {
      tl.kill();
    };
    // shouldSnap is stable per table and read at run time — adding it as a dep
    // would re-run (and so re-play) the flip. See hooks/useTableSnap.
    // `card` IS a dep: a value change must reconcile the visuals (guarded from
    // re-flipping by hasRevealedRef above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailRef, skipFlip, card]);

  // A card with no parseable face renders NOTHING — not a back. The back is
  // only ever the transient concealment of a real face mid-flip. Painting it
  // for a faceless card made a broken value pixel-identical to a legitimate
  // face-down card, which is how shipped garbage masqueraded as game state.
  // (Unity does the equivalent: face-down board cards get alpha=0.)
  const face = cardFaceSrc(rank, suit);

  return (
    <div style={BaseStyle}>
      {face && (
        <>
          <Paper
            ref={frontRef}
            elevation={0}
            style={{
              ...SideStyle,
              ...(isDimmed && darkenCard)
            }}>
            <img src={face} alt={cardAltText(rank, suit)} draggable={false} style={ArtStyle} />
          </Paper>
          <Paper ref={backRef} elevation={0} style={SideStyle}>
            <img src={CARD_BACK_SRC} alt={CARD_BACK_ALT} draggable={false} style={ArtStyle} />
          </Paper>
        </>
      )}
    </div>
  );
};

export default CommunityCard;

const darkenCard: CSSProperties = {
  filter: 'brightness(0.4)'
};

const BaseStyle: CSSProperties = {
  // The board's size comes from its wrapper's `scale()` (HoldEmCards), which
  // rides `--seat-scale` — and that now carries the player's Table View Size.
  // Sizing the box here as well applied the setting twice, and did it on the
  // BOX rather than the transform, so the board's footprint and its painted
  // size disagreed.
  position: 'relative',
  width: `${SMALL_CARD_WIDTH}px`,
  height: `${SMALL_CARD_HEIGHT}px`
};

// One side of the card — the artwork paints the face (or back) including its
// white fill and rounded corners; the box only carries the shadow, rounded to
// the artwork's own corners so it follows them at every Table View Size.
const SideStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  height: '100%',
  width: '100%',
  background: 'transparent',
  borderRadius: `${cardCornerRadiusForWidth(SMALL_CARD_WIDTH)}px`,
  boxShadow: '0 1px 2px rgba(0,0,0,0.12), 0 4px 8px rgba(0,0,0,0.08)'
};

const ArtStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  userSelect: 'none'
};
